import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {
    DataSource,
    IList,
    IListItem,
    IListItemState,
    ListEvent,
    ListEventType,
    ListModel
} from '@mintdata/mint-kit/api/models/List';
import {IStyles} from '@mintdata/mint-kit/impl/util/Styles';
import {bind} from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import _extend from 'lodash/extend';

const switcherStyles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/switcher/Switcher.less');

export type ItemRenderer<T> = (data:T, state:IListItemState) => JSX.Element;
export type SelectItemHandler<T> = (data:T) => void;

export interface ISwitcherItemData<T = any>
{
    label?:string;
    value?:T;
    isSelected?:boolean
}

export interface ISwitcherProps<T>
{
    dataSource:DataSource<T>;
    itemRenderer?:ItemRenderer<T>;
    onItemSelect?:SelectItemHandler<T>;
    theme?:IStyles;
}

@observer
export class Switcher<T> extends React.Component <ISwitcherProps<T>>
{
    @observable private _listModel:IList<T> = null;
    @observable private _styles:IStyles = {};

    @bind
    public itemRenderer(data:any, state:IListItemState):JSX.Element
    {
        const {label} = data as ISwitcherItemData;
        const cx = classnames.bind(this._styles);
        const {isSelected} = state;

        return <div className={cx(this._styles.item, {active: isSelected})}>
            {label}
        </div>;
    }

    @bind
    private onListEvent(e:ListEvent<T>):void
    {
        const isSelectionEvent = (e.type === ListEventType.ItemsSelected);
        const {onItemSelect} = this.props;

        if( isSelectionEvent && onItemSelect )
        {
            const selectedItem = e.items[0];
            onItemSelect(selectedItem.getData());
        }

        this.forceUpdate();
    }

    @action
    private _initDataSource(dataSource:DataSource<T>):void
    {
        const listModel:IList<T> = dataSource instanceof ListModel
            ? dataSource
            : new ListModel<T>(dataSource as Array<T>);

        listModel.onListEvent(this.onListEvent);

        this._listModel = listModel;
    }

    @action
    private _initStyles(styles:IStyles):void
    {
        this._styles = _extend({}, switcherStyles, styles);
    }

    @action
    public componentDidMount():void
    {
        const {dataSource, theme} = this.props;

        this._initDataSource(dataSource);
        this._initStyles(theme);
    }

    @action
    public componentWillReceiveProps(newProps:ISwitcherProps<T>):void
    {

        const {dataSource, theme} = this.props;

        if( dataSource !== newProps.dataSource )
            this._initDataSource(newProps.dataSource);

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    @bind
    private _onClickItem(itemId:GUID)
    {
        this._listModel
            .unselectAll(false)
            .selectById(itemId);
    }

    private _renderItem(item:IListItem<T>):JSX.Element
    {
        const {itemRenderer = this.itemRenderer} = this.props;
        const itemData:T = item.getData();
        const itemState:IListItemState = item.getState();

        return (
            <div
                key={item.getUid()}
                onClick={() =>
                {
                    this._onClickItem(item.getUid());
                }}
            >
                {
                    itemRenderer(itemData, itemState)
                }
            </div>
        );
    }

    public render():JSX.Element
    {
        return (
            <div className={this._styles.base}>
                {
                    this._listModel && this._listModel
                        .getItems()
                        .map((item:IListItem<T>) => this._renderItem(item))
                }
            </div>
        );
    }
}

export type SwitcherConstructor<T> = new() => Switcher<T>;
